import { cx, useTranslations } from "@jugl-web/utils";
import { useInvoicesSettingsPageContext } from "@web-src/modules/invoices/pages/InvoicesSettingsPage/context/useInvoicesSettingsPageContext";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { CompanyAvatarField } from "./components/CompanyAvatarField";
import { CompanyNameField } from "./components/CompanyNameField";
import { StreetField } from "./components/StreetField";
import { StateField } from "./components/StateField";
import { CityField } from "./components/CityField";
import { CountryField } from "./components/CountryField";
import { ZipCodeField } from "./components/ZipCodeField";
import { CustomFieldButton } from "./components/CustomFieldButton";
import { CustomField } from "./components/CustomField";
import { TaxField } from "./components/TaxField";
import { NotesField } from "./components/NotesField";
import { DueInField } from "./components/DueInField";

export const InvoiceSettingsForm = () => {
  const { entity } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const {
    register,
    errors,
    watch,
    setCustomFields,
    customFields,
    setLogo,
    logo,
    addTaxField,
    setTaxFieldAsDefault,
    taxFields,
    editTaxField,
    removeTaxField,
    configId,
  } = useInvoicesSettingsPageContext();

  return (
    <div
      className={cx(
        "jugl__custom-scrollbar overflow-y-auto overflow-x-visible py-8",
        {
          "max-h-[calc(100vh-16.2rem)]": !configId,
          "max-h-[calc(100vh-11.3rem)]": configId,
        }
      )}
    >
      <div className="rounded-2xl bg-white py-2 pr-4">
        <div className="px-8 py-6">
          <h2 className="font-secondary m-0 text-base font-medium">
            {t({
              id: "invoices.settings.businessInformation.preview.title",
              defaultMessage: "Bill from default",
            })}
          </h2>

          <form action="" className="mt-8">
            <CompanyAvatarField onSave={setLogo} value={logo} />

            <div className="mt-8 space-y-4">
              <CompanyNameField
                register={register}
                errors={errors}
                value={watch("name")}
              />
              <StreetField
                register={register}
                errors={errors}
                value={watch("street") || ""}
              />
              <div className="grid grid-cols-2 gap-4">
                <StateField
                  register={register}
                  errors={errors}
                  value={watch("state") || ""}
                />
                <CountryField
                  register={register}
                  errors={errors}
                  value={watch("country") || ""}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CityField
                  register={register}
                  errors={errors}
                  value={watch("city") || ""}
                />

                <ZipCodeField
                  register={register}
                  errors={errors}
                  value={watch("zipCode") || ""}
                />
              </div>

              {customFields.map(({ name, value }) => (
                <CustomField
                  key={name}
                  value={value}
                  name={name}
                  onChange={(newValue) => {
                    setCustomFields(
                      customFields.map((field) =>
                        name === field.name
                          ? { ...field, value: newValue }
                          : field
                      )
                    );
                  }}
                  onRemove={() => {
                    setCustomFields(
                      customFields.filter((field) => field.name !== name)
                    );
                  }}
                />
              ))}
            </div>

            <div className="mt-8">
              <CustomFieldButton
                onSave={(name: string, value: string) => {
                  setCustomFields([...customFields, { name, value }]);
                }}
              />
            </div>

            {/* TODO temporary hidden 22.06.2025, for future releases */}
            {/* <div className="mt-16">
              <TaxField
                type="settings"
                level="item"
                fields={taxFields.item}
                onAdd={(item) => addTaxField("item", item)}
                onSetDefault={(id) => setTaxFieldAsDefault("item", id)}
                onEdit={(field) => editTaxField("item", field)}
                onRemove={(name) => removeTaxField("item", name)}
                currency={entity.currency}
              />
            </div> */}

            <div className="mt-16">
              <TaxField
                type="settings"
                fields={taxFields.invoice}
                onAdd={(item) => addTaxField("invoice", item)}
                onToggle={(id, isSelected) =>
                  setTaxFieldAsDefault("invoice", id, isSelected)
                }
                onEdit={(field) => editTaxField("invoice", field)}
                onRemove={(name) => removeTaxField("invoice", name)}
                currency={entity.currency}
                title={t({
                  id: "invoices.settings.businessInformation.taxSettings.invoiceLevelTax",
                  defaultMessage: "Invoice level Tax",
                })}
              />
            </div>

            {/* TODO temporary hidden 22.06.2025, for future releases */}
            {/* <div className="mt-16">
              <PrefixField
                register={register}
                errors={errors}
                value={watch("prefix")}
              />
            </div> */}

            <div className="mt-16">
              <DueInField />
            </div>

            <div className="my-16">
              <NotesField />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
